import { ValidatorType } from '@data-driven-forms/react-form-renderer';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, ProgressBar } from 'react-bootstrap';
import { XCircleFill, Trash } from 'react-bootstrap-icons';
import { DropzoneOptions, FileError, FileRejection, useDropzone } from 'react-dropzone';
import { AnyType, FilePreview } from '../interfaces';
import { LoadingIndicator } from './LoadingIndicator';
import { rawHtmlProps } from '../utils';

export interface HcpUploadProps {
  buttonAlign?: 'left' | 'right';
  disabled?: boolean;
  dropzoneOptions: DropzoneOptions;
  onChange?: (files: FilePreview[]) => void;
  value?: FilePreview[];
  isLoading?: boolean;
  label?: string;
  icon?: JSX.Element;
  buttonClassName?: string;
  buttonVariant?: string;
  wrapperDivClassName?: string;
  maxFiles?: number;
  validate?: ValidatorType[];
  showRequiredText?: boolean;
  name?: string;
  showHelperText: boolean;
  showGenericFileName: boolean;
  showDocumentDate: boolean;
  fileSizeError?: AnyType | { label?: string };
  fileTypeError?: AnyType | { label?: string };
  maxFilesError?: AnyType | { label?: string };
  showNewFileBadge: boolean;
}

export const RequiredText = () => {
  return <span className='text-danger'>Required</span>;
};

export const HcpUpload = (props: HcpUploadProps) => {
  const [files, setFiles] = useState<FilePreview[]>([]);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [totalFileSize, setTotalFileSize] = useState(0);

  const showRequiredText = useMemo(
    () => props?.validate?.some((o) => props.showRequiredText && o.type == 'required'),
    [props.validate]
  );
  if (props?.maxFiles && !props.dropzoneOptions.maxFiles) {
    props['dropzoneOptions']['maxFiles'] = props.maxFiles;
  }

  useEffect(() => {
    if (props.value) {
      setFiles(props.value);
    }
  }, [props.value]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles?.length > (props.dropzoneOptions?.maxFiles ?? 1)) {
        return;
      }

      // Set processing state to true and reset progress
      setIsProcessingFile(true);
      setUploadProgress(0);

      let processedCount = 0;
      const totalFiles = acceptedFiles.length;

      const formattedFiles = acceptedFiles.map((acceptedFile: File, fileIndex: number) => {
        const preview = URL.createObjectURL(acceptedFile);
        const sizeInMB = (acceptedFile.size / (1024 * 1024)).toFixed(2); // Size in MB with two decimal places
        let filePayload: Partial<FilePreview> = { ...acceptedFile, preview, bytes: null, size: Number(sizeInMB) };

        const reader = new FileReader();

        // Track actual file reading progress
        reader.onprogress = (event) => {
          if (event.lengthComputable) {
            // Calculate progress for this specific file
            const currentFileProgress = (event.loaded / event.total) * 100;

            if (totalFiles === 1) {
              // For single file, show direct progress
              setUploadProgress(Math.round(currentFileProgress));
            } else {
              // For multiple files, calculate weighted progress
              const baseProgress = (processedCount / totalFiles) * 100;
              const currentFileWeight = (1 / totalFiles) * 100;
              const weightedCurrentProgress = (currentFileProgress / 100) * currentFileWeight;
              setUploadProgress(Math.round(baseProgress + weightedCurrentProgress));
            }
          }
        };

        reader.readAsDataURL(acceptedFile);

        reader.onloadend = () => {
          const bytes = reader?.result;
          filePayload = Object.assign(filePayload, { bytes });

          // Increment processed count
          processedCount++;

          // If all files are processed, show completion and clear state
          if (processedCount === totalFiles) {
            setUploadProgress(100);
            setTimeout(() => {
              setIsProcessingFile(false);
              setUploadProgress(0);
            }, 500); // Slightly longer delay to show 100% completion
          }
        };

        // Handle potential read errors
        reader.onerror = () => {
          processedCount++;

          if (processedCount === totalFiles) {
            setTimeout(() => {
              setIsProcessingFile(false);
              setUploadProgress(0);
            }, 300);
          }
        };

        return filePayload;
      });

      const accumulateFiles = [...formattedFiles, ...files].splice(0, props.dropzoneOptions?.maxFiles ?? 1);

      setFiles(accumulateFiles);
      if (props.onChange) props.onChange(accumulateFiles);
    },
    [files, props]
  );

  const { fileRejections, getRootProps, getInputProps, open } = useDropzone({
    onDrop: props.dropzoneOptions?.onDrop ?? onDrop,
    noClick: props.dropzoneOptions?.noClick ?? true,
    noKeyboard: props.dropzoneOptions?.noKeyboard ?? true,
    accept: props.dropzoneOptions?.accept ?? '.pdf',
    multiple: (props.dropzoneOptions?.maxFiles ?? 1) > 1,
    maxFiles: props.dropzoneOptions?.maxFiles ?? 1,
    maxSize: props.dropzoneOptions?.maxSize ?? 10000000, // kb to mb
    ...props.dropzoneOptions
  });

  const removeFile = (file: FilePreview) => () => {
    const newFiles = [...files];
    newFiles.splice(newFiles.indexOf(file), 1);
    setFiles(newFiles);
    props.onChange && props.onChange(newFiles);
  };

  const fileErrors = fileRejections.reduce(
    (acc: AnyType, rejection: FileRejection) => {
      if (rejection.errors.find((e: FileError) => e.code === 'file-invalid-type')) {
        return { ...acc, invalid: [...acc.invalid, rejection] };
      }
      if (rejection.errors.find((e: FileError) => e.code === 'file-too-large')) {
        return { ...acc, tooLarge: [...acc.tooLarge, rejection] };
      }
      if (rejection.errors.find((e: FileError) => e.code === 'too-many-files')) {
        return { ...acc, tooMany: [...acc.tooMany, rejection] };
      }
    },
    {
      invalid: [],
      tooLarge: [],
      tooMany: []
    }
  );

  // Calculate total file size including rejected files for error messages
  const getTotalFileSizeForError = () => {
    let currentFilesSize = 0;
    let rejectedFilesSize = 0;

    // Add size from current files
    files.forEach((file) => {
      if (typeof file?.size === 'number') {
        currentFilesSize += file.size;
      }
    });

    // Add size from rejected files
    fileRejections.forEach((rejection) => {
      if (rejection.file?.size) {
        rejectedFilesSize += rejection.file.size / (1024 * 1024); // Convert bytes to MB
      }
    });

    return (currentFilesSize + rejectedFilesSize).toFixed(2);
  };

  const errors = fileRejections.length > 0 && (
    <ul className='text-danger fs-2'>
      {fileErrors.invalid.length > 0 && (
        <li>{props?.fileTypeError?.label || 'Please upload a supported file-type (.pdf)'}</li>
      )}
      {fileErrors.tooLarge.length > 0 && (
        <li>
          {props?.fileSizeError?.label
            ? props.fileSizeError.label.replace('$$REPLACE_TOTAL_FILE_SIZE$$', getTotalFileSizeForError())
            : 'The maximum file size is 10 MB. Please try again.'}
        </li>
      )}
      {fileErrors.tooMany.length > 0 && (
        <li>
          {props?.maxFilesError?.label ||
            `You've exceeded the maximum number of files. Please upload ${props.dropzoneOptions?.maxFiles} documents or less.`}
        </li>
      )}
    </ul>
  );

  useEffect(() => {
    let totalSize = 0;
    if (files.length > 0) {
      files.forEach((file) => {
        if (typeof file?.size === 'number') {
          totalSize += file.size;
        }
      });
    }
    setTotalFileSize(Number(totalSize.toFixed(2)));
  }, [files]);

  const fileBadges = (showNewFileBadge: boolean) =>
    files.map((file: FilePreview, i: number) =>
      showNewFileBadge ? (
        <React.Fragment key={i}>
          <i className='fa-light fa-file text-secondary'></i>
          <span className='text-truncate text-secondary ms-2'>{file?.path} </span>
          <Trash className='pointer ms-3' color='black' onClick={removeFile(file)} />
        </React.Fragment>
      ) : (
        <Badge key={i} pill bg='primary' className='p-2 mb-1'>
          <span className='text-truncate'>{file?.path}</span>
          <XCircleFill className='pointer ms-2' color='white' onClick={removeFile(file)} />
        </Badge>
      )
    );

  return props.name === 'copay_claim_support_document_upload' ||
    props.name === 'insurance_upload_insurance_card' ||
    props.name === 'secondary_upload_insurance_card' ? (
    <div>
      <div
        {...getRootProps()}
        className={`d-flex align-items-center justify-content-center`}
        style={{ gap: 10, border: '1px #ADB5BD solid', borderRadius: 2, width: 440, height: 158 }}>
        <div className='drop-upload__wrapper text-center'>
          <div className='drop-upload__space px-2 py-1'>
            <Button
              className='mt-n2 mb-2 button_font-12 px-2 py-1 cmx__file_upload__button-upload-file'
              variant='transparent'
              disabled={props.disabled || files?.length >= (props.dropzoneOptions?.maxFiles ?? 1)}
              onClick={open}>
              {props.isLoading && <LoadingIndicator />}
              <svg width='48' height='48' viewBox='0 0 48 48' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <rect width='48' height='48' fill='white' fillOpacity='0.01' />
                <path
                  d='M14.94 12.0021C14.7151 12.0024 14.4933 12.0532 14.2907 12.1509C14.0882 12.2485 13.9102 12.3904 13.77 12.5661L4.61996 24.0021H18C18.3978 24.0021 18.7793 24.1601 19.0606 24.4414C19.3419 24.7227 19.5 25.1042 19.5 25.5021C19.5 26.6955 19.9741 27.8401 20.818 28.684C21.6619 29.528 22.8065 30.0021 24 30.0021C25.1934 30.0021 26.338 29.528 27.1819 28.684C28.0259 27.8401 28.5 26.6955 28.5 25.5021C28.5 25.1042 28.658 24.7227 28.9393 24.4414C29.2206 24.1601 29.6021 24.0021 30 24.0021H43.38L34.23 12.5661C34.0897 12.3904 33.9117 12.2485 33.7092 12.1509C33.5066 12.0532 33.2848 12.0024 33.06 12.0021H14.94ZM44.802 27.0021H31.35C31.0057 28.6975 30.0859 30.2218 28.7463 31.3166C27.4068 32.4115 25.73 33.0096 24 33.0096C22.2699 33.0096 20.5931 32.4115 19.2536 31.3166C17.9141 30.2218 16.9942 28.6975 16.65 27.0021H3.19796L4.15796 34.6881C4.20335 35.0513 4.37999 35.3854 4.65462 35.6274C4.92925 35.8694 5.2829 36.0027 5.64896 36.0021H42.351C42.7165 36.002 43.0694 35.8684 43.3434 35.6264C43.6174 35.3845 43.7936 35.0508 43.839 34.6881L44.799 27.0021H44.802ZM11.427 10.6911C11.8488 10.1638 12.3838 9.73818 12.9924 9.4458C13.6011 9.15342 14.2677 9.00176 14.943 9.00206H33.057C33.7322 9.00176 34.3988 9.15342 35.0075 9.4458C35.6161 9.73818 36.1512 10.1638 36.573 10.6911L47.673 24.5661C47.7977 24.7226 47.8898 24.9025 47.9439 25.0952C47.998 25.2879 48.013 25.4895 47.988 25.6881L46.818 35.0601C46.6819 36.1487 46.1528 37.1502 45.3302 37.8761C44.5076 38.6021 43.4481 39.0025 42.351 39.0021H5.64896C4.55182 39.0025 3.49233 38.6021 2.6697 37.8761C1.84708 37.1502 1.318 36.1487 1.18196 35.0601L0.0119566 25.6881C-0.0126321 25.4893 0.00283789 25.2876 0.0574541 25.0949C0.11207 24.9022 0.204726 24.7224 0.329957 24.5661L11.43 10.6911H11.427Z'
                  fill='#115099'
                />
              </svg>
            </Button>

            <p className='form-label' {...rawHtmlProps(props.label ?? 'ATTACH')} />
            <input {...getInputProps()} />

            {props.showHelperText && (
              <p className='drop-upload__help-text' style={{ fontSize: '14px', color: '#6C757D' }}>
                <small>
                  Accepted format is: PDF <br />
                  You may upload up to {props?.dropzoneOptions?.maxFiles} documents.
                </small>
              </p>
            )}
          </div>
        </div>
      </div>
      <p className='mt-2'>{!props.showDocumentDate && fileBadges(props.showNewFileBadge)} </p>
      {isProcessingFile && !errors && (
        <div className='mt-2' style={{ width: '100%' }}>
          <ProgressBar now={uploadProgress} striped variant='success' className='mt-1' style={{ height: '8px' }} />
        </div>
      )}
      {errors}
    </div>
  ) : (
    <div {...getRootProps()} className={`d-flex align-items-center`} style={{ gap: 10 }}>
      <div className={props?.wrapperDivClassName}>
        <input {...getInputProps()} />
        <div className='badge-left'>{fileBadges(true)}</div>
        <Button
          className={`mr-2 ${props?.buttonClassName}`}
          variant={props?.buttonVariant ?? 'outline-secondary-new'}
          size='sm'
          disabled={props.disabled || files?.length >= (props.dropzoneOptions?.maxFiles ?? 1)}
          onClick={open}>
          {props.isLoading && <LoadingIndicator spinnerProps={{ size: 'sm', className: 'mr-2' }} />}
          {props?.icon ?? props.label ?? 'ATTACH'}
        </Button>
        {showRequiredText && <RequiredText />}

        <div className='badge-right mt-2'>{fileBadges(false)}</div>
        {errors}
      </div>
    </div>
  );
};

export const getAcceptFiles = (accpetFilesArray: string | string[]): string => {
  const tempArray: string[] = typeof accpetFilesArray === 'string' ? accpetFilesArray?.split(',') : accpetFilesArray;
  let formattedFilesString = '';
  if (tempArray && tempArray?.length > 0) {
    tempArray?.forEach((file: AnyType, index: number) => {
      const fileExt = file?.replaceAll('.', '');
      formattedFilesString += index !== 0 ? (tempArray?.length - 1 === index ? `, & ` : `, `) + fileExt : fileExt;
    });
  }
  return formattedFilesString;
};
